import { ChangeDetectionStrategy, Component, HostListener, computed, input, output } from '@angular/core';

import type { CrmContact } from '@integrations/crm-contacts';

import type { LoginEduCheckboxLegacy } from '@sites/entity-core';
import type { LoginEduForm } from '@sites/entity-signal-form';

import { EAttributeTypesCrm } from '@common/enums';

import { WebEditorModule } from '@web/shared/components/web-editor/web-editor.module';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';

/**
 * Settings panel for one consent checkbox. Reads and writes
 * `checkboxes[index]` straight on the widget signal form; the parent control
 * owns the transaction — save/cancel are emitted up.
 */
@Component({
    selector: 'web-checkbox-button-settings',
    templateUrl: './checkbox-button-settings.component.html',
    styleUrls: ['./checkbox-button-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, WebEditorModule],
})
export class CheckboxButtonSettingsComponent {
    public readonly form = input.required<LoginEduForm>();
    public readonly index = input.required<number>();
    public readonly crmContactsVariablesList = input<CrmContact[]>([]);

    public readonly saveEvent = output<void>();
    public readonly cancelEvent = output<void>();

    private readonly checkbox = computed<LoginEduCheckboxLegacy | undefined>(
        () => this.form().checkboxes().value()[this.index()]
    );

    public readonly label = computed(() => this.checkbox()?.label ?? '');
    public readonly required = computed(() => Boolean(this.checkbox()?.required));
    public readonly crmContactVariable = computed(() => this.checkbox()?.crmContactVariable ?? null);

    /** The `Validators.required` of the FormGroup era. */
    public readonly hasCrmVariableError = computed(() => {
        const value = this.crmContactVariable();
        return value === null || value === '';
    });

    /**
     * Variables already mapped by another checkbox are off the list — including
     * this one's own, which the template re-adds as a standalone option so the
     * current selection stays visible.
     */
    public readonly filteredCrmContactsVariables = computed<CrmContact[]>(() => {
        const used = new Set(
            this.form()
                .checkboxes()
                .value()
                .map((checkbox) => checkbox.crmContactVariable)
        );

        return this.crmContactsVariablesList().filter(
            (field) => !used.has(field.id) && field.type !== EAttributeTypesCrm.multiselect
        );
    });

    // --- Setters (template event handlers) ---

    public saveLabel(label: string): void {
        this.patch({ label });
    }

    public toggleRequired(value: boolean): void {
        this.patch({ required: value });
    }

    public setCrmVariableData(event: Event): void {
        const raw = (event.target as HTMLSelectElement).value;
        // A numeric option is a CRM attribute id (and carries its type); the
        // built-in contact fields come through as their plain name.
        const variable = this.crmContactsVariablesList().find((item) => item.id === Number(raw));

        this.patch({
            crmContactVariable: variable ? variable.id : raw,
            crmVariableType: variable ? variable.type : null,
        });
    }

    // --- Presentation helpers ---

    public getVariableName(id: string | number): string {
        return this.crmContactsVariablesList().find((variable) => variable.id === id)?.name || id.toString();
    }

    public isDisabledVariable(variable: CrmContact): boolean {
        return variable.type !== EAttributeTypesCrm.checkbox && variable.type !== EAttributeTypesCrm.text;
    }

    public onSave(): void {
        this.saveEvent.emit();
    }

    public onReset(): void {
        this.cancelEvent.emit();
    }

    @HostListener('document:keydown.enter')
    public onEnterKeyPress(): void {
        this.onSave();
    }

    @HostListener('document:keydown.escape', ['$event'])
    public onEscapeKeyPress(event: KeyboardEvent): void {
        event.stopPropagation();
        this.onReset();
    }

    private patch(patch: Partial<LoginEduCheckboxLegacy>): void {
        this.form().updateCheckbox(this.index(), patch);
    }
}
